
import React, { useState, useEffect } from 'react';
import { LineChart, BarChart, Users, MessageSquare } from 'lucide-react';
import Card from '@/components/ui/Card';
import { Alert, AlertDescription } from '@/components/ui/Alert';
import { useAuth } from '../../../context/AuthContext';

const StatsCard = ({ title, value, icon: Icon, trend, trendValue }) => (
  <Card className="p-6">
    <div className="flex justify-between items-start">
      <div>
        <p className="text-sm font-medium text-gray-600">{title}</p>
        <h3 className="text-2xl font-bold mt-2">{value}</h3>
        {trend && (
          <p className={`text-sm mt-2 ${
            trend === 'up' ? 'text-green-600' : 'text-red-600'
          }`}>
            {trend === 'up' ? '↑' : '↓'} {trendValue}% from last month
          </p>
        )}
      </div>
      <div className="p-3 bg-primary-50 rounded-lg">
        <Icon className="w-6 h-6 text-primary-600" />
      </div>
    </div>
  </Card>
);

const Overview = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('No authentication token found');
        }

        const response = await fetch(`${import.meta.env.VITE_APP_API_URL}/dashboard/overview`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }

        });

        if (!response.ok) {
            if (response.status === 401) {
              throw new Error('Authentication expired. Please login again.');
            }
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to fetch dashboard data');
          }
  

        const data = await response.json();
        const transformedData = {
            totalCustomers: data.stats?.totalCustomers?.toLocaleString() || '0',
            customerGrowth: data.stats?.customerGrowth || 0,
            totalAppointments: data.stats?.totalAppointments?.toLocaleString() || '0',
            appointmentGrowth: data.stats?.appointmentGrowth || 0,
            totalRevenue: data.stats?.totalRevenue 
              ? `$${data.stats.totalRevenue.toLocaleString()}`
              : '$0',
            revenueGrowth: data.stats?.revenueGrowth || 0,
            messagesSent: data.stats?.messagesSent?.toLocaleString() || '0',
            messageGrowth: data.stats?.messageGrowth || 0
          };
  
          setStats(transformedData);
  
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    
    if (user) {
        fetchDashboardData();
      }
    }, [user]);
  

  if (loading) {
    return <div>Loading...</div>;
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive" className="mb-6">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }





  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatsCard
          title="Total Customers"
          value={stats.totalCustomers}
          icon={Users}
          trend="up"
          trendValue={stats.customerGrowth}
        />
        
        <StatsCard
          title="Monthly Appointments"
          value={stats.totalAppointments}
          icon={LineChart}
          trend="up"
          trendValue={stats.appointmentGrowth}
        />
        
        <StatsCard
          title="Monthly Revenue"
          value={stats.totalRevenue}
          icon={BarChart}
          trend="up"
          trendValue={stats.revenueGrowth}
        />
        
        <StatsCard
          title="Messages Sent"
          value={stats.messagesSent}
          icon={MessageSquare}
          trend={stats.messageGrowth >= 0 ? "up" : "down"}
          trendValue={Math.abs(stats.messageGrowth)}
        />
      </div>

      {/* Add more sections like recent activity, charts, etc. */}
    </div>
  );
};

export default Overview;